/**
 * Web implementation of EditAccountPageService
 * Integrates with existing authStore, backend API, and validation systems
 */

import { useAuthStore, useDeliveryStore, useCartStore, UserService, validateFullName, getCurrentUser as getSupabaseUser } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import type { ClientUser } from '@restaurant/core'
import type {
  IEditAccountPageService,
  AccountFormData,
  ProfileValidationResult
} from '../../contracts'
import { birthdayDisplayToISO } from '../../shared/birthday'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Web-specific edit account page service implementation
 * Uses authStore for local state and ApiClient for backend API calls
 */
export class WebEditAccountPageService implements IEditAccountPageService {
  private userService = new UserService({ apiClient })

  private get authStore() {
    return useAuthStore.getState()
  }

  async getCurrentUserProfile(): Promise<ClientUser | null> {
    const storeUser = this.authStore.user
    if (!storeUser) return null

    if (!storeUser.birthday) {
      try {
        const supabaseUser = await getSupabaseUser()
        const birthday = supabaseUser?.user_metadata?.birthday || ''
        if (birthday) {
          this.authStore.updateProfile({ birthday } as Partial<ClientUser>)
          return { ...storeUser, birthday }
        }
      } catch {
        // Supabase fetch failed — use store as-is
      }
    }

    return storeUser
  }

  /**
   * Update user profile with new data
   * Calls individual PATCH endpoints for each field, then updates local store
   */
  async updateUserProfile(formData: AccountFormData): Promise<ClientUser> {
    const currentUser = this.authStore.user
    if (!currentUser) {
      throw new Error('No authenticated user found')
    }

    // Parse full name into first and last name
    const nameParts = (formData.fullName ?? '').trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Track what changed to only update what's necessary
    const updates: Partial<ClientUser> = {}

    // Only send non-empty names. The backend rejects an empty first_name /
    // last_name (min_length=1) with a 422, so never PATCH a blank value.
    if (firstName && firstName !== currentUser.firstName) {
      await apiClient.patch('/users/first_name', { first_name: firstName })
      updates.firstName = firstName
    }

    // Update last name if changed
    if (lastName && lastName !== currentUser.lastName) {
      await apiClient.patch('/users/last_name', { last_name: lastName })
      updates.lastName = lastName
    }

    // Update email if changed
    if (formData.emailAddress !== currentUser.email) {
      try {
        await apiClient.patch('/users/email', { email: formData.emailAddress })
        updates.email = formData.emailAddress
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.message || 'Failed to update email'
        throw new Error(message)
      }
    }

    // Note: Phone number is read-only on the edit account page. Birthday is
    // set-once: persisted only when the account doesn't have one yet (support
    // ticket d8939f1b — legacy accounts signed up before birthday collection
    // had no way to set it). Once stored it stays read-only.
    if (formData.birthday && !currentUser.birthday) {
      const birthdayISO = birthdayDisplayToISO(formData.birthday)
      if (birthdayISO) {
        await apiClient.put('/auth/profile', { birthday: birthdayISO })
        updates.birthday = birthdayISO
      }
    }

    // Update local auth store with the changes
    if (Object.keys(updates).length > 0) {
      this.authStore.updateProfile(updates)
    }

    // Return updated user
    const updatedUser = this.authStore.user
    if (!updatedUser) {
      throw new Error('Failed to update user profile')
    }

    return updatedUser
  }

  /**
   * Validate profile data before saving
   */
  async validateProfileData(formData: AccountFormData): Promise<ProfileValidationResult> {
    const errors: Partial<Record<keyof AccountFormData, string>> = {}

    // Validate full name — require both a first and last name (backend rejects
    // empty last_name → 422, and Clover rejects a blank last name at card-add).
    // Canonical rule lives in validateFullName so signup and card-add pre-flight
    // stay in agreement.
    const nameError = validateFullName(formData.fullName)
    if (nameError) {
      errors.fullName = nameError
    }

    // Validate email address
    if (!formData.emailAddress || formData.emailAddress.trim() === '') {
      errors.emailAddress = 'Email address is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.emailAddress)) {
        errors.emailAddress = 'Please enter a valid email address'
      } else if (formData.emailAddress.length > 254) {
        errors.emailAddress = 'Email address is too long'
      }
    }

    // No phoneNumber validation on purpose. The phone number is the loyalty
    // account's identity anchor, so self-serve changing is deliberately
    // unsupported — the field is rendered read-only (see
    // EditAccountPageViewModel's fieldConfig) and updateUserProfile never
    // sends it. Validating a value the user cannot edit could only ever block
    // them from saving their name or email, never help. Changing a number is a
    // support-mediated request; do not re-add this.

    // Validate birthday (optional but if provided must be valid).
    // Birthday in display format ("Jan 12") is read-only and was validated at sign-up — skip.
    if (formData.birthday && formData.birthday.trim() !== '') {
      const isDisplayFormat = /^[A-Z][a-z]{2}\s\d{1,2}$/.test(formData.birthday.trim())
      if (!isDisplayFormat) {
        const birthdayDate = new Date(formData.birthday)
        const today = new Date()

        if (isNaN(birthdayDate.getTime())) {
          errors.birthday = 'Please enter a valid date'
        } else if (birthdayDate > today) {
          errors.birthday = 'Birthday cannot be in the future'
        } else {
          const thirteenYearsAgo = new Date()
          thirteenYearsAgo.setFullYear(today.getFullYear() - 13)

          if (birthdayDate > thirteenYearsAgo) {
            errors.birthday = 'You must be at least 13 years old'
          }
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    }
  }

  /**
   * Sign out the current user
   */
  async signOutUser(): Promise<void> {
    useDeliveryStore.getState().clearAddresses()
    useCartStore.getState().clearCart()
    this.authStore.logout()
  }

  /**
   * Delete the current user's account permanently
   * Calls backend API to delete from Supabase, then logs out locally
   */
  async deleteUserAccount(): Promise<void> {
    try {
      await this.userService.deleteAccount()
      useDeliveryStore.getState().clearAddresses()
      useCartStore.getState().clearCart()
      this.authStore.logout()
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Account deletion failed', { domain: 'auth', error: String(error) })
      throw error
    }
  }
}
